<script setup lang="ts">
import { computed } from "vue";
import { twMerge } from "tailwind-merge";

import { LoadingCircle } from "@/shared/components/svgs";
import { useDynamicIcon } from "@/shared/composables/useDynamicIcon";
import type { ButtonProps, ButtonEmits } from "@/shared/components/button/ButtonModel";

const props = withDefaults(defineProps<ButtonProps>(), {
    isInTable: false,
    responsiveMobileWidth: false,
    isWidthFull: false,
    disabled: false,
    type: "button",
    iconWidth: 24,
    iconHeight: 24,
});

const emits = defineEmits<ButtonEmits>();

// Use dynamic icon loading composable
const { loadIcon, isIconAvailable, getAvailableIcons } = useDynamicIcon();

const iconComponent = computed(() => {
    if (!props.icon) return null;

    // Check if icon is available
    if (!isIconAvailable(props.icon)) {
        console.warn(`Icon "${props.icon}" not found. Available icons:`, getAvailableIcons());
        return null;
    }

    // Dynamically load the icon component
    return loadIcon(props.icon);
});

// 기본 스타일 계산
const getBaseStyles = () =>
    "flex items-center justify-center gap-[0.5rem] text-center transition-all font-pretended font-medium whitespace-nowrap";

// 크기 스타일 계산
const getSizeStyles = () => {
    if (props.isInTable) return "px-[1.4rem] py-[0.6rem] text-base rounded-[0.8rem]";
    if (props.isWidthFull) return "w-full py-[1.3rem] text-[2rem] rounded-[1.2rem]";

    const sizeStyle = {
        xlarge: "px-[2rem] h-[6.4rem] gap-3 rounded-large1 text-base",
        large: "px-[1.6rem] h-[5.6rem] gap-3 rounded-medium1 text-base",
        medium: "px-[1.4rem] h-[4.8rem] gap-2 rounded-medium1 text-base",
        small: "px-[1.2rem] h-[4rem] gap-2 rounded-medium1 text-base",
        xsmall: "px-[0.8rem] h-[1.6rem] gap-2 rounded-small1 text-base",
    };

    return props.size ? sizeStyle[props.size] : "px-[2rem] py-[1.05rem] text-base rounded-[0.8rem]";
};

// 너비 스타일 계산
const getWidthStyles = () => {
    if (props.responsiveMobileWidth) return "w-full sm:w-fit";
    if (props.isWidthFull) return "w-full";

    return "w-fit";
};

// 색상 스타일 계산
const getColorStyles = () => {
    if (props.disabled) return "bg-secondary-gray text-primary-gray";

    const colorStyles = {
        primary:
            "bg-primary-red text-white cursor-pointer active:bg-secondary-red active:text-primary-red",
        "primary-border":
            "bg-white text-primary-red border border-primary-red cursor-pointer active:bg-primary-red active:text-white",
        "primary-border-blue":
            "bg-white text-primary-blue border border-primary-blue cursor-pointer active:bg-primary-blue active:text-white",
        normal: "bg-white text-primary-black border border-secondary-gray cursor-pointer active:bg-secondary-gray active:border-primary-black",
        "primary-blue":
            "bg-white text-primary-blue border border-primary-blue cursor-pointer active:bg-secondary-blue active:border-primary-blue",
        "primary-yellow":
            "bg-white text-primary-yellow border border-primary-yellow cursor-pointer active:bg-secondary-yellow active:border-primary-yellow",
    };

    return colorStyles[props.color] || "";
};

// 최종 스타일 계산
const baseStyles = computed(() => {
    return twMerge(getBaseStyles(), getSizeStyles(), getWidthStyles(), getColorStyles());
});

const handleClick = (event: Event) => {
    event.stopPropagation();
    emits("click");
};
</script>

<template>
    <button :type="type" :class="baseStyles" :disabled="disabled" @click="handleClick">
        <div v-if="icon" class="flex justify-center items-center">
            <component
                :is="iconComponent"
                :width="iconWidth"
                :height="iconHeight"
                :color="iconColor"
            />
        </div>
        <div v-if="!icon && loading" class="flex justify-center items-center">
            <LoadingCircle />
        </div>
        {{ value }}
    </button>
</template>

<style scoped></style>
